'use strict'

import BasketMgr from '../custom-api/basket-custom-api.js'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '../hooks/use-multi-site'

/**
 * Calculates the total discounts applied to a given basket.
 *
 * This function computes the sum of all discounts applied at both the order level
 * and the product level within the provided basket object. It iterates through
 * order-level price adjustments and product-level price adjustments, excluding
 * any adjustments related to bonus product line items.
 *
 * @param {Object} basket - The basket object containing order and product details.
 * @param {Array} basket.orderPriceAdjustments - An array of order-level price adjustments.
 * @param {Array} basket.productItems - An array of product items in the basket.
 * @param {Array} basket.productItems[].priceAdjustments - An array of price adjustments for each product.
 * @param {boolean} basket.productItems[].bonusProductLineItem - A flag indicating if the product is a bonus item.
 *
 * @returns {number} The total amount of discounts applied to the basket.
 */
export const calculateTotalDiscounts = (basket) => {
    let totalDiscount = 0

    // Calculate order-level discounts
    if (basket.orderPriceAdjustments) {
        basket.orderPriceAdjustments.forEach((adjustment) => {
            totalDiscount += adjustment.price
        })
    }

    // Calculate product-level discounts
    if (basket.productItems) {
        basket.productItems.forEach((product) => {
            if (product.priceAdjustments && !product.bonusProductLineItem) {
                product.priceAdjustments.forEach((adjustment) => {
                    totalDiscount += adjustment.price
                })
            }
        })
    }

    return totalDiscount
}

/**
 * Retrieves the default shipment from a given line item container.
 * If there is only one shipment, it returns that shipment. If there are multiple shipments,
 * it searches for a shipment marked as the default and returns it.
 *
 * @param {Object} lineItemCtr - The line item container object containing shipments.
 * @param {Array} lineItemCtr.shipments - An array of shipment objects within the line item container.
 * @returns {Object|undefined} - The default shipment object if found, otherwise undefined.
 */
export const getDefaultShipment = (lineItemCtr) => {
    if (!lineItemCtr || !lineItemCtr.shipments) {
        return {}
    }

    const shipments = lineItemCtr?.shipments
    if (shipments.length === 1) {
        return shipments[0]
    }

    for (let i = 0; i < shipments.length; i++) {
        if (shipments[i].c_useAsDefault) {
            return shipments[i]
        }
    }
}

/**
 * Retrieves the default shipment ID from a given line item container.
 *
 * This function extracts the default shipment associated with the provided
 * line item container and returns its shipment ID. If no default shipment
 * is found, the function returns undefined.
 *
 * @param {Object} lineItemCtr - The line item container from which to retrieve the default shipment.
 * @returns {string|undefined} The shipment ID of the default shipment, or undefined if no default shipment exists.
 */
export const getDefaultShipmentID = (lineItemCtr) => {
    return getDefaultShipment(lineItemCtr)?.shipmentId
}

/**
 * Calculates and returns an estimated delivery date range or a single delivery date based on the provided shipping method.
 *
 * @param {Object} shippingMethod - The shipping method object containing calculated shipping dates.
 * @param {string} shippingMethod.c_calculatedShippingDateFrom - The start date for the estimated shipping range.
 * @param {string} shippingMethod.c_calculatedShippingDateTo - The end date for the estimated shipping range.
 * @param {string} localeID - The locale identifier used for formatting the date.
 * @param {Object} intl - The internationalization object used for formatting messages.
 * @returns {string|null} A formatted string indicating the estimated delivery date range or a single delivery date, or null if no dates are available.
 */
export const getEstimationDate = (shippingMethod, localeID, intl) => {
    const {
        c_calculatedShippingDateFrom: calculatedShippingDateFrom,
        c_calculatedShippingDateTo: calculatedShippingDateTo
    } = shippingMethod || {}

    /**
     * Determines the ordinal suffix for a given day of the month.
     *
     * This function calculates the appropriate ordinal suffix ('st', 'nd', 'rd', 'th')
     * for a given day of the month. The suffix is determined based on the rules of
     * English ordinal numbers. For example, 1 becomes '1st', 2 becomes '2nd', 3 becomes '3rd',
     * and numbers ending in 4-9 or 0, as well as numbers from 11 to 13, receive the 'th' suffix.
     *
     * @param {number} day - The day of the month for which the ordinal suffix is needed.
     * @returns {string} The ordinal suffix corresponding to the given day.
     */
    const ordinalSuffix = (day) => {
        if (day > 3 && day < 21) return 'th'
        switch (day % 10) {
            case 1:
                return 'st'
            case 2:
                return 'nd'
            case 3:
                return 'rd'
            default:
                return 'th'
        }
    }

    /**
     * Formats a given date string into a localized date format with ordinal suffixes for English locales.
     *
     * @param {string} dateString - The date string to be formatted.
     * @returns {string} - The formatted date string according to the specified locale, with ordinal suffixes if the locale is English.
     *
     * @example
     * // Assuming localeID is 'en-US' and options are set for date formatting:
     * // formatDate('2023-10-05') might return 'October 5th, 2023'.
     *
     * @example
     * // Assuming localeID is 'fr-FR' and options are set for date formatting:
     * // formatDate('2023-10-05') might return '5 octobre 2023'.
     *
     * @note
     * This function relies on the global variables `localeID` and `options` for determining the locale and formatting options.
     * The function also assumes the existence of an `ordinalSuffix` function that returns the appropriate ordinal suffix for a given day.
     */
    const formatDate = (dateString) => {
        const date = new Date(dateString)
        const options = {day: 'numeric', month: 'long'}
        let formattedDate = new Intl.DateTimeFormat(localeID, options).format(date)

        if (localeID.startsWith('en')) {
            const day = date.getDate()
            formattedDate =
                new Intl.DateTimeFormat(localeID, {month: 'long'}).format(date) +
                ' ' +
                `${day}${ordinalSuffix(day)}`
        }

        return formattedDate
    }

    if (calculatedShippingDateFrom && calculatedShippingDateTo) {
        const startingDate = formatDate(calculatedShippingDateFrom)
        const endDateFormatted = formatDate(calculatedShippingDateTo)

        return intl.formatMessage(
            {
                id: 'checkout_confirmation.estimated.delivery.range',
                defaultMessage: 'Estimated delivery between {startingDate} and {endDate}'
            },
            {
                startingDate,
                endDate: endDateFormatted
            }
        )
    } else if (calculatedShippingDateTo) {
        const endDate = formatDate(calculatedShippingDateTo)

        return intl.formatMessage(
            {
                id: 'checkout_confirmation.estimated.delivery.single',
                defaultMessage: 'Estimated delivery on {endDate}'
            },
            {
                endDate
            }
        )
    }

    return null
}

/**
 * Validates the given address object to ensure all required fields are present.
 *
 * This function checks if the provided address object contains all necessary
 * fields. It logs a console assertion if the address is invalid.
 *
 * @param {Object} addressObj - The address object to validate.
 * @returns {boolean} - Returns true if the address is valid, otherwise false.
 */
export const isValidAddress = (addressObj) => {
    return (
        addressObj &&
        addressObj.firstName &&
        addressObj.lastName &&
        addressObj.address1 &&
        addressObj.city &&
        addressObj.countryCode &&
        addressObj.phone
    )
}

/**
 * Initializes and returns a new instance of BasketMgr with the appropriate parameters.
 *
 * This function utilizes the `useMultiSite` and `getConfig` hooks to retrieve the current
 * site and locale information, as well as the application configuration. It then constructs
 * a new `BasketMgr` object with these parameters.
 *
 * @returns {BasketMgr} An instance of BasketMgr initialized with the current site, organization,
 *                      and locale parameters.
 */
export const useInitializeBasketMgr = () => {
    const {site, locale} = useMultiSite()
    const {app: appConfig} = getConfig()

    return new BasketMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })
}
